import { z } from 'zod';
import {
  paginatedResponse,
  paginationQuery,
  nonNegativeDecimalString as decimalString,
} from './common';

/**
 * Schemas for the dedicated `Lighting` catalog table — Romanian "iluminat"
 * (LED strips, profiles, drivers, sensors, …).
 *
 * `pricePerPiece` is RON canonical. Source data: `excels/products.xlsx`
 * sheet `Iluminat`.
 *
 * Names are prefixed with `catalog` to avoid collision with the existing
 * project-line `LightingItem` schemas in `project-lighting.ts`.
 */

export const catalogLightingCurrencyEnum = z.enum(['RON', 'EUR', 'USD']);
export type CatalogLightingCurrencyValue = z.infer<typeof catalogLightingCurrencyEnum>;

const optionalTrim = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .optional()
    .transform((v) => (v === '' ? undefined : v));

export const catalogLightingListItem = z.object({
  id: z.string(),
  name: z.string(),
  offerName: z.string().nullable(),
  sku: z.string().nullable(),
  internalCode: z.string().nullable(),
  manufacturer: z.string().nullable(),
  pricePerPiece: decimalString.optional(),
  currency: catalogLightingCurrencyEnum,
  active: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
  deletedAt: z.string().nullable(),
});
export type CatalogLightingListItem = z.infer<typeof catalogLightingListItem>;

export const catalogLightingDetails = catalogLightingListItem.extend({
  attributes: z.unknown().nullable(),
});
export type CatalogLightingDetails = z.infer<typeof catalogLightingDetails>;

export const listCatalogLightingQuery = paginationQuery.extend({
  search: optionalTrim(120),
  manufacturer: optionalTrim(120),
  active: z
    .union([z.boolean(), z.literal('true'), z.literal('false')])
    .transform((v) => (typeof v === 'boolean' ? v : v === 'true'))
    .optional(),
  includeDeleted: z
    .union([z.boolean(), z.literal('true'), z.literal('false')])
    .transform((v) => (typeof v === 'boolean' ? v : v === 'true'))
    .optional()
    .default(false),
});
export type ListCatalogLightingQuery = z.infer<typeof listCatalogLightingQuery>;

export const listCatalogLightingResponse = paginatedResponse(catalogLightingListItem);
export type ListCatalogLightingResponse = z.infer<typeof listCatalogLightingResponse>;

export const createCatalogLightingRequest = z.object({
  name: z.string().trim().min(1).max(200),
  offerName: optionalTrim(200),
  sku: optionalTrim(80),
  internalCode: optionalTrim(120),
  manufacturer: optionalTrim(120),
  pricePerPiece: decimalString,
  currency: catalogLightingCurrencyEnum.default('RON'),
  active: z.boolean().default(true),
  attributes: z.unknown().optional(),
});
export type CreateCatalogLightingRequest = z.infer<typeof createCatalogLightingRequest>;

export const updateCatalogLightingRequest = createCatalogLightingRequest.partial();
export type UpdateCatalogLightingRequest = z.infer<typeof updateCatalogLightingRequest>;
